/**
 * What each governed semantic view offers the query builder: its metrics,
 * dimensions and date fields, spelled `table.member` exactly as the view
 * exposes them and as the agent's playbooks list them
 * (services/ai/skills/fly_api/{deliverability,site_activity,profile_changes,pipeline_health}.md).
 *
 * The catalog lives in the client for now so the form can offer members the
 * agent did not pick. The server compiles and validates every spec, so a stale
 * entry here can only produce a compile error the modal shows, never a wrong
 * query. Serving this from the server is a later slice.
 */

export type FieldType = 'string' | 'number' | 'bool' | 'date' | 'enum';

export interface ViewCatalog {
  view: string;
  label: string;
  /** Dotted date dimensions, the first being the one a new time window uses. */
  dateFields: string[];
  /** Dotted metric names. Every metric is numeric and filters by its bare name. */
  metrics: string[];
  /** Dotted dimension names, date fields included. */
  dimensions: string[];
  /** Known small value sets, by bare member name. */
  enums: Record<string, string[]>;
}

const CHANNELS = ['email', 'push', 'sms', 'in_app', 'webhook', 'slack', 'line', 'inbox', 'whatsapp', 'urban_airship'];
const SOURCE_TYPES = ['campaign', 'broadcast', 'transactional', 'other'];

function members(table: string, names: string): string[] {
  return names
    .split(/\s+/)
    .filter(Boolean)
    .map((name) => `${table}.${name}`);
}

const deliverability: ViewCatalog = {
  view: 'sv_deliverability',
  label: 'Deliverability',
  dateFields: [
    'deliveries.send_date',
    'deliveries.occurred_date',
    'messages.send_date',
    'attributed_events.occurred_date',
    'segment_events.occurred_date',
    'inbound_messages.occurred_date',
    'webhook_events.occurred_date',
  ],
  metrics: [
    ...members(
      'deliveries',
      `sent delivered opened human_opened clicked human_clicked converted bounced unsubscribed spammed failed
       unique_sent unique_delivered unique_opens unique_human_opens unique_clicks unique_human_clicks unique_converted
       unique_bounced unique_unsubscribed unique_spammed
       delivery_rate open_rate human_open_rate click_rate human_click_rate ctor human_ctor bounce_rate conversion_rate
       unsubscribe_rate spam_rate
       unique_delivery_rate unique_open_rate unique_human_open_rate unique_click_rate unique_human_click_rate
       unique_bounce_rate unique_conversion_rate unique_unsubscribe_rate unique_spam_rate unique_ctor unique_human_ctor`,
    ),
    ...members(
      'messages',
      `messages_sent people_messaged messages_delivered messages_opened messages_clicked message_open_rate
       message_click_rate messages_delivered_not_opened messages_opened_not_clicked people_not_converted
       people_opened_not_clicked avg_minutes_to_open avg_minutes_to_click median_minutes_to_open`,
    ),
    ...members(
      'attributed_events',
      'events people_did_event attributed_to_message people_attributed avg_hours_since_message',
    ),
    ...members('segment_events', 'entries exits people_entered people_exited net_change'),
    ...members('inbound_messages', 'inbound_received people_replied replies_to_deliveries'),
    ...members('webhook_events', 'webhooks_received distinct_webhook_events'),
  ],
  dimensions: [
    ...members(
      'deliveries',
      `occurred_date send_date channel source_type metric_name is_machine is_synthetic person_id inbox_provider
       inbox_domain mx_host email_client device_type link_url link_id reason frequency_cap_name occurred_hour send_hour
       content_id`,
    ),
    ...members('campaigns', 'campaign_id campaign_name campaign_type'),
    ...members('actions', 'action_id action_name action_type action_tracked action_holdout action_enabled'),
    ...members('broadcasts', 'newsletter_id broadcast_name broadcast_tracked'),
    ...members('templates', 'template_name subject_line template_language'),
    ...members('transactional_messages', 'transactional_name'),
    ...members(
      'messages',
      `send_date channel source_type person_id send_hour content_id inbox_provider bounce_reason was_delivered
       was_opened was_human_opened was_clicked was_converted was_bounced`,
    ),
    ...members(
      'attributed_events',
      `occurred_date event_name person_id was_attributed attributed_channel hours_since_message ingress_source_type
       ingress_source_subtype cdp_source_type cdp_source_name`,
    ),
    ...members('segment_events', 'occurred_date membership_change person_id'),
    ...members('segments', 'segment_name segment_type segment_archived'),
    ...members('tags', 'tag_id tag_name tag_deleted'),
    ...members('inbound_messages', 'occurred_date channel keyword person_id'),
    ...members('webhook_events', 'occurred_date webhook_event_name'),
  ],
  enums: {
    channel: CHANNELS,
    attributed_channel: CHANNELS,
    source_type: SOURCE_TYPES,
    device_type: ['Mobile', 'Desktop', 'Tablet', 'Bot', 'Unrecognized'],
    membership_change: ['entered', 'exited'],
    segment_type: ['data_driven', 'manual', 'temporary', 'hidden'],
  },
};

const siteActivity: ViewCatalog = {
  view: 'sv_site_activity',
  label: 'Site & app activity',
  dateFields: ['activity.occurred_date', 'geofence.occurred_date'],
  metrics: [
    ...members(
      'activity',
      `page_views screen_views total_views unique_visitors unique_page_visitors unique_screen_visitors
       distinct_pages_viewed views_per_visitor`,
    ),
    ...members('geofence', 'geofence_entries geofence_exits people_in_geofences'),
  ],
  dimensions: [
    ...members('activity', 'occurred_date occurred_hour view_type view_name page_host page_path person_id'),
    ...members('geofence', 'occurred_date movement geofence_name geoset_id person_id'),
  ],
  enums: {
    view_type: ['page', 'screen'],
    movement: ['enter', 'exit'],
  },
};

const profileChanges: ViewCatalog = {
  view: 'sv_profile_changes',
  label: 'Profile changes',
  dateFields: ['profile_lifecycle.occurred_date', 'attribute_changes.occurred_date', 'device_changes.occurred_date'],
  metrics: [
    ...members(
      'profile_lifecycle',
      'profiles_created profiles_deleted profiles_merged anonymous_merges net_profile_change',
    ),
    ...members('attribute_changes', 'changes people_changed attributes_changed'),
    ...members(
      'device_changes',
      'devices_added devices_removed device_updates unique_devices people_with_device_changes',
    ),
  ],
  dimensions: [
    ...members(
      'profile_lifecycle',
      `occurred_date lifecycle_event person_id source_type source_subtype source_campaign_id source_action_id
       source_import_id cdp_source_type cdp_source_name`,
    ),
    ...members('attribute_changes', 'occurred_date attribute_name from_value to_value person_id'),
    ...members('device_changes', 'occurred_date change_type device_attribute device_platform person_id'),
  ],
  enums: {
    lifecycle_event: ['created', 'deleted', 'merged', 'anonymous_merged'],
    change_type: ['added', 'updated', 'removed'],
  },
};

const pipelineHealth: ViewCatalog = {
  view: 'sv_pipeline_health',
  label: 'Data pipeline health',
  dateFields: ['pipeline_events.occurred_date', 'segment_builds.occurred_date'],
  metrics: [
    ...members('pipeline_events', 'failures skips total_events people_affected'),
    ...members('segment_builds', 'builds segments_built build_entries build_exits'),
  ],
  dimensions: [
    ...members(
      'pipeline_events',
      'occurred_date event_category outcome failure_detail reason failure_subject person_id',
    ),
    ...members('segment_builds', 'occurred_date segment_id'),
    ...members('segments', 'segment_name segment_type'),
  ],
  enums: {
    outcome: ['failed', 'skipped'],
    segment_type: ['data_driven', 'manual', 'temporary', 'hidden'],
  },
};

const CATALOGS: Record<string, ViewCatalog> = Object.fromEntries(
  [deliverability, siteActivity, profileChanges, pipelineHealth].map((catalog) => [catalog.view, catalog]),
);

export function findViewCatalog(view: string): ViewCatalog | null {
  return CATALOGS[view] ?? null;
}

/** The bare output name of a `table.member`. */
export function bareName(member: string): string {
  const dot = member.lastIndexOf('.');
  return dot === -1 ? member : member.slice(dot + 1);
}

/**
 * The type of a member, for picking operators and the value control. Metrics
 * are numbers; dimensions are typed by what the playbooks say about them,
 * which their names carry: `*_date`, `is_*`/`was_*`/`*_tracked`, ids and hours.
 */
export function fieldType(catalog: ViewCatalog | null, field: string): FieldType {
  const name = bareName(field);
  if (catalog != null && catalog.metrics.some((m) => m === field || bareName(m) === name)) return 'number';
  if (catalog?.enums[name] != null) return 'enum';
  if (name.endsWith('_date')) return 'date';
  if (/^(is|was)_|_(tracked|holdout|enabled|archived|deleted)$/.test(name)) return 'bool';
  if (/(^|_)id$|_hour$|hours_since_message$|_ids$/.test(name)) return 'number';
  return 'string';
}

/**
 * Whether a metric is a rate, a 0-1 fraction rather than a count: every
 * `*_rate` member, and the click-to-open rates (`ctor`, `human_ctor`,
 * `unique_ctor`). Named in the catalog's own spelling, so a member added under
 * either pattern is read as a rate without an entry here.
 */
export function isRateMetric(member: string): boolean {
  return /(^|_)(rate|ctor)$/.test(bareName(member).toLowerCase());
}
